import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useErrorBoundary, useViewer } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	FlexGap,
	GraphQLErrorMessage,
	Heading3,
	IconWrapper,
	Paragraph,
	StarsIcon,
	TextArea,
	tokens,
} from '@treams/treams-ui';

const Wrapper = styled(Box)`
	background: ${tokens.brand.background};
	border: 1px solid ${tokens.brand.border};
	border-radius: ${tokens.radius.sm}px;
`;

interface IProps {
	featureName: graphql.FeatureName;
	targetID: string;
	targetType: graphql.FeatureRatingTargetType;
	i18nPrefix: string;
	sampleRate?: number;
}

export const FeatureRatingSection = ({
	featureName,
	targetID,
	targetType,
	i18nPrefix,
	sampleRate = 1,
}: IProps) => {
	const { t } = useTranslation();
	const viewer = useViewer();

	const [isSampled] = useState(() => Math.random() < sampleRate);

	const [createFeatureRating, { loading: createFeatureRatingLoading }] =
		graphql.useCreateFeatureRatingMutation();
	const [updateFeatureRating, { loading: updateFeatureRatingLoading }] =
		graphql.useUpdateFeatureRatingMutation();

	const ratedAfter = useMemo(() => moment().subtract(1, 'month').startOf('day').toISOString(), []);

	const { data, loading } = graphql.useHasViewerRatedQuery({
		variables: {
			viewerID: viewer!.id,
			featureName,
			targetType,
			ratedAfter,
		},
		skip: !viewer || !isSampled,
	});

	const [ratingID, setRatingID] = useState<string | undefined>(undefined);
	const [rating, setRating] = useState<boolean | null>(null);
	const [ratingSuccess, setRatingSuccess] = useState(false);

	const [ratingText, setRatingText] = useState<string | undefined>(undefined);
	const [showRatingText, setShowRatingText] = useState(false);

	const [error, attempt] = useErrorBoundary();

	if (!isSampled || loading || !viewer || !data || data.hasViewerRated) {
		return null;
	}

	const onRate = attempt(async (rating: boolean) => {
		setRating(rating);

		const result = await createFeatureRating({
			variables: {
				input: {
					viewerID: viewer!.id,
					featureName,
					targetID,
					targetType,
					binaryValue: rating,
				},
			},
		});

		return result.data?.createFeatureRating.featureRating?.id;
	});

	const onPositiveRate = attempt(async () => {
		const ratingID = await onRate(true);

		if (ratingID) {
			setRatingSuccess(true);
		}
	});

	const onNegativeRate = attempt(async () => {
		const ratingID = await onRate(false);

		if (ratingID) {
			setRatingID(ratingID);

			setShowRatingText(true);
		}
	});

	const onSubmitRatingText = attempt(async () => {
		if (!ratingID || !ratingText) {
			return;
		}

		const result = await updateFeatureRating({
			variables: {
				input: {
					id: ratingID,
					text: ratingText,
				},
			},
		});

		if (result.data?.updateFeatureRating.featureRating?.id) {
			setRatingSuccess(true);
		}
	});

	return (
		<AnimatePresence>
			<motion.div
				initial={{ opacity: 0, height: 0 }}
				animate={{ opacity: ratingSuccess ? 0 : 1, height: ratingSuccess ? 0 : 'auto' }}
				exit={{ opacity: 0, height: 0 }}
				transition={{ duration: 0.2, ease: 'easeInOut' }}
				key={`${i18nPrefix}-feedback`}
			>
				<Wrapper py={3} px={4} mb={3}>
					<GraphQLErrorMessage error={error} />
					<FlexGap gap={tokens.gap.inline} alignItems="center">
						<IconWrapper size={20} color={tokens.brand.icon} flexShrink={0}>
							<StarsIcon />
						</IconWrapper>
						<Heading3>{t(`${i18nPrefix}.title`)}</Heading3>
					</FlexGap>
					<Paragraph mt={`${tokens.gap.inline}px`} maxWidth="500px">
						{t(`${i18nPrefix}.description`)}
					</Paragraph>
					<FlexGap gap={20} alignItems="center" mt={12}>
						{showRatingText && (
							<Box>
								<Paragraph mb={2}>{t(`${i18nPrefix}.followup`)}</Paragraph>
								<FlexGap gap={8} alignItems="flex-end">
									<Box flexGrow={1} width={400}>
										<TextArea
											minRows={1}
											maxRows={5}
											autoFocus
											value={ratingText}
											onChange={(value) => setRatingText(value)}
										/>
									</Box>
									<Button
										title={t('common.send')}
										type="secondary"
										maxWidth={100}
										onClick={onSubmitRatingText}
										loading={updateFeatureRatingLoading}
										maxHeight={40}
										disabled={ratingText === undefined || ratingText.trim() === ''}
									/>
								</FlexGap>
							</Box>
						)}
						<FlexGap gap={4}>
							{!showRatingText && (
								<AnimatePresence>
									<>
										<motion.div
											initial={{ opacity: 0, width: 'auto' }}
											animate={{
												opacity: rating === false ? 0 : 1,
												width: rating === false ? 0 : 'auto',
											}}
											exit={{ opacity: 0, width: 0 }}
										>
											<Button
												title={rating === false ? ' ' : t(`${i18nPrefix}.positive`)}
												type="secondary"
												minWidth={100}
												onClick={onPositiveRate}
												loading={createFeatureRatingLoading && Boolean(rating)}
											/>
										</motion.div>
										<motion.div
											initial={{ opacity: 0, width: 'auto' }}
											animate={{
												opacity: rating === true ? 0 : 1,
												width: rating === true ? 0 : 'auto',
											}}
											exit={{ opacity: 0, width: 0 }}
										>
											<Button
												title={rating === true ? ' ' : t(`${i18nPrefix}.negative`)}
												type="secondary"
												minWidth={100}
												onClick={onNegativeRate}
												loading={createFeatureRatingLoading && rating === false}
											/>
										</motion.div>
									</>
								</AnimatePresence>
							)}
						</FlexGap>
					</FlexGap>
				</Wrapper>
			</motion.div>

			<motion.div
				style={{ pointerEvents: 'none' }}
				initial={{ opacity: 0, height: 0 }}
				animate={{ opacity: ratingSuccess ? 1 : 0, height: ratingSuccess ? 'auto' : 0 }}
				exit={{ opacity: 0, height: 0 }}
				transition={{ duration: 0.2, ease: 'easeInOut' }}
				key={`${i18nPrefix}-feedback-success`}
			>
				<Box pb={3}>
					<Callout color="green">{t(`${i18nPrefix}.thanks`)}</Callout>
				</Box>
			</motion.div>
		</AnimatePresence>
	);
};
